export type CrmPurpose = 'OPENING' | 'REPLACEMENT' | 'INCREMENT';
export type CrmPhase = 'FILES' | 'PREVIEW' | 'CONFIRM' | 'RESULT' | 'UNKNOWN';
export interface CrmViewModel {
  purpose: CrmPurpose | 'MEMBERS';
  phase: CrmPhase;
  dirty: boolean;
  busy: boolean;
  unresolved: boolean;
  canPreview: boolean;
  canConfirm: boolean;
}
export interface CrmRecoveryItem {
  purpose: CrmPurpose;
  key: string;
  identity: string | null;
  corrupt: boolean;
}
export interface CrmGuard {
  newWritesAllowed: boolean;
  scope: string;
  generation: number;
  operation: number;
  busy: boolean;
  dirty: boolean;
  unresolved: boolean;
}
export function createCrmGuard(scope: string, verified = false): CrmGuard {
  return {
    newWritesAllowed: verified,
    scope,
    generation: 0,
    operation: 0,
    busy: false,
    dirty: false,
    unresolved: !verified,
  };
}
export function changeCrmScope(guard: CrmGuard, scope: string) {
  if (guard.scope === scope) return;
  guard.scope = scope;
  guard.generation++;
  guard.operation++;
  guard.busy = false;
  guard.dirty = false;
  // A new identity must establish its own recovery state before starting anything.
  guard.unresolved = true;
  guard.newWritesAllowed = false;
}
export function beginCrmOperation(guard: CrmGuard) {
  if (guard.busy) return null;
  guard.busy = true;
  return { generation: guard.generation, operation: ++guard.operation };
}
export function endCrmOperation(
  guard: CrmGuard,
  token: NonNullable<ReturnType<typeof beginCrmOperation>>,
) {
  if (token.generation !== guard.generation || token.operation !== guard.operation) return false;
  guard.busy = false;
  return true;
}
export function canLeaveCrm(guard: CrmGuard, confirmDiscard: () => boolean) {
  return !guard.busy && !guard.unresolved && (!guard.dirty || confirmDiscard());
}
/** Only three exact legacy keys. Malformed content is retained and blocks new work. */
export function readCrmRecovery(
  storage: Pick<Storage, 'getItem'>,
  actor: string,
  venue: string,
  environment: string,
): CrmRecoveryItem[] {
  if (!actor || !venue || !environment) throw new Error('Trusted CRM context required');
  const keys: Array<[CrmPurpose, string]> = [
    ['OPENING', `opening-attempts:${actor}:${venue}:${environment}`],
    ['INCREMENT', `crm-increment-attempts:${actor}:${venue}:${environment}`],
    ['REPLACEMENT', `crm-replacement:${actor}:${venue}`],
  ];
  return keys.flatMap<CrmRecoveryItem>(([purpose, key]) => {
    try {
      const raw = storage.getItem(key);
      if (raw === null) return [];
      const value: unknown = JSON.parse(raw);
      if (purpose === 'REPLACEMENT') {
        if (
          !value ||
          typeof value !== 'object' ||
          !('operationKey' in value) ||
          !('planHash' in value) ||
          typeof value.operationKey !== 'string' ||
          !/^[a-f0-9-]{36}$/i.test(value.operationKey) ||
          typeof value.planHash !== 'string' ||
          !/^[a-f0-9]{64}$/.test(value.planHash)
        )
          throw new Error('Corrupt recovery');
        return [{ purpose, key, identity: value.operationKey, corrupt: false }];
      }
      if (
        !Array.isArray(value) ||
        value.length > 10 ||
        value.some((v) => typeof v !== 'string' || !/^[a-f0-9]{64}$/.test(v))
      )
        throw new Error('Corrupt recovery');
      return value.map((identity: string) => ({ purpose, key, identity, corrupt: false }));
    } catch {
      return [{ purpose, key, identity: null, corrupt: true }];
    }
  });
}
